import React from 'react';

import {
    IScrollAwareTapPressEventHandlers,
    IScrollAwareTapPressEventHandlersArgs,
} from '../interfaces';

const DEFAULT_TOUCH_MOVE_THRESHOLD_PX = 8;
const DEFAULT_SUPPRESS_MOUSE_AFTER_TOUCH_MS = 700;

interface IGestureStart {
    clientX: number,
    clientY: number,
    identifier: number,
}

const pointerEventsAreSupported = (): boolean => (
    typeof window !== 'undefined'
    && typeof window.PointerEvent !== 'undefined'
);

const getTouchByIdentifier = (
    touches: React.TouchList,
    identifier?: number,
): React.Touch | undefined => {
    for (let index = 0; index < touches.length; index += 1) {
        const touch = touches.item(index);
        if (
            touch
            && (identifier === undefined || touch.identifier === identifier)
        ) {
            return touch;
        }
    }

    return undefined;
};

const movementExceedsThreshold = ({
    start,
    clientX,
    clientY,
    thresholdPx,
}: {
    start: IGestureStart | null,
    clientX: number,
    clientY: number,
    thresholdPx: number,
}): boolean => {
    if (!start) return false;

    return (
        Math.abs(clientX - start.clientX) > thresholdPx
        || Math.abs(clientY - start.clientY) > thresholdPx
    );
};

/**
 * Builds press handlers for expandable rows that should open on a deliberate tap,
 * while letting touch scroll gestures pass without toggling the row.
 */
const getScrollAwareTapPressEventHandlers = ({
    onTap,
    suppressMouseAfterTouchMs = DEFAULT_SUPPRESS_MOUSE_AFTER_TOUCH_MS,
    touchMoveThresholdPx = DEFAULT_TOUCH_MOVE_THRESHOLD_PX,
}: IScrollAwareTapPressEventHandlersArgs): IScrollAwareTapPressEventHandlers => {
    let gestureStart: IGestureStart | null = null;
    let gestureMovedPastThreshold = false;
    let lastTouchInteractionTimestamp = 0;

    const markTouchInteraction = (): void => {
        lastTouchInteractionTimestamp = Date.now();
    };

    const resetGesture = (): void => {
        gestureStart = null;
        gestureMovedPastThreshold = false;
    };

    const handleTouchStart = (event: React.TouchEvent): void => {
        markTouchInteraction();
        const touch = getTouchByIdentifier(event.changedTouches);
        if (!touch) {
            resetGesture();
            return;
        }

        gestureStart = {
            identifier: touch.identifier,
            clientX: touch.clientX,
            clientY: touch.clientY,
        };
        gestureMovedPastThreshold = false;
    };

    const handleTouchMove = (event: React.TouchEvent): void => {
        markTouchInteraction();
        const touch = getTouchByIdentifier(
            event.changedTouches,
            gestureStart?.identifier,
        );
        if (!touch) return;

        if (movementExceedsThreshold({
            start: gestureStart,
            clientX: touch.clientX,
            clientY: touch.clientY,
            thresholdPx: touchMoveThresholdPx,
        })) {
            gestureMovedPastThreshold = true;
        }
    };

    const handleTouchEnd = (event: React.TouchEvent): void => {
        markTouchInteraction();
        const touch = getTouchByIdentifier(
            event.changedTouches,
            gestureStart?.identifier,
        );
        const touchEndedAfterScroll = (
            gestureMovedPastThreshold
            || (
                touch
                && movementExceedsThreshold({
                    start: gestureStart,
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                    thresholdPx: touchMoveThresholdPx,
                })
            )
        );

        resetGesture();
        if (touchEndedAfterScroll) return;

        onTap(event);
    };

    const handleTouchCancel = (): void => {
        markTouchInteraction();
        resetGesture();
    };

    const handleMouseDown = (event: React.MouseEvent): void => {
        if (event.button !== 0) return;

        const recentTouchInteraction = (
            Date.now() - lastTouchInteractionTimestamp < suppressMouseAfterTouchMs
        );
        if (recentTouchInteraction) return;

        onTap(event);
    };

    const handlePointerDown = (event: React.PointerEvent): void => {
        if (event.pointerType === 'mouse') {
            if (event.button !== 0) return;
            onTap(event);
            return;
        }

        markTouchInteraction();
        gestureStart = {
            identifier: event.pointerId,
            clientX: event.clientX,
            clientY: event.clientY,
        };
        gestureMovedPastThreshold = false;
    };

    const handlePointerMove = (event: React.PointerEvent): void => {
        if (!gestureStart || event.pointerId !== gestureStart.identifier) return;

        markTouchInteraction();
        if (movementExceedsThreshold({
            start: gestureStart,
            clientX: event.clientX,
            clientY: event.clientY,
            thresholdPx: touchMoveThresholdPx,
        })) {
            gestureMovedPastThreshold = true;
        }
    };

    const handlePointerUp = (event: React.PointerEvent): void => {
        if (!gestureStart || event.pointerId !== gestureStart.identifier) return;

        markTouchInteraction();
        const pointerEndedAfterScroll = (
            gestureMovedPastThreshold
            || movementExceedsThreshold({
                start: gestureStart,
                clientX: event.clientX,
                clientY: event.clientY,
                thresholdPx: touchMoveThresholdPx,
            })
        );

        resetGesture();
        if (pointerEndedAfterScroll) return;

        onTap(event);
    };

    const handlePointerCancel = (): void => {
        markTouchInteraction();
        resetGesture();
    };

    if (pointerEventsAreSupported()) {
        return {
            onPointerCancel: handlePointerCancel,
            onPointerDown: handlePointerDown,
            onPointerMove: handlePointerMove,
            onPointerUp: handlePointerUp,
        };
    }

    return {
        onMouseDown: handleMouseDown,
        onTouchCancel: handleTouchCancel,
        onTouchEnd: handleTouchEnd,
        onTouchMove: handleTouchMove,
        onTouchStart: handleTouchStart,
    };
};

export default getScrollAwareTapPressEventHandlers;
